import { Layers } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { EmptyState } from '@/ui/EmptyState';
import { PageHeader } from '@/ui/PageHeader';
import { Checkbox } from '@/ui/base/checkbox';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';

import { ListaDeContagens, QuadroDeEstado, Secao } from './componentes';
import type { ItemDaLegenda, MapaInterativo } from './contrato';
import { carregarMapa } from './dados';
import { useColecao } from './useColecao';

/**
 * ONDA-CL3 — mapa interativo. Tela única, sem aba.
 *
 * **A camada cartográfica não vem nesta PR, e agora se sabe qual é.** O
 * protótipo desenha com Leaflet sobre OpenStreetMap — o rodapé do mapa dele diz
 * "Leaflet | © OpenStreetMap". Trazer isso é acrescentar dependência de mapa e
 * uma fonte externa de ladrilhos, e `CLAUDE.md` ("Stack") exige decisão
 * registrada antes. Vem a estrutura inteira — filtro, camadas, resumo e legenda
 * — e no lugar do desenho fica dito o que falta e por quê.
 *
 * É o mesmo critério da coluna `Saúde` na Parametrização: a estrutura fica, a
 * afirmação que ninguém apurou não.
 */

const TODAS_AS_DRES = 'todas';

function mapaVazio(dado: MapaInterativo): boolean {
  return dado.resumo.length === 0 && dado.legenda.length === 0;
}

const COR_DA_LEGENDA: Record<ItemDaLegenda['cor'], string> = {
  primaria: 'bg-primary',
  atencao: 'bg-warning',
  critica: 'bg-destructive',
};

export function MapaInterativoPage() {
  const [parametros, definirParametros] = useSearchParams();
  const dreEscolhida = parametros.get('dre') ?? TODAS_AS_DRES;
  const { estado, recarregar } = useColecao(carregarMapa, mapaVazio);

  function escolherDre(valor: string) {
    definirParametros((atual) => {
      const proximo = new URLSearchParams(atual);
      if (valor === TODAS_AS_DRES) {
        proximo.delete('dre');
      } else {
        proximo.set('dre', valor);
      }
      return proximo;
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Análises e notificações"
        title="Mapa Interativo"
        description="Visualização geográfica das DREs, escolas e demandas"
      />

      <QuadroDeEstado
        estado={estado}
        recarregar={recarregar}
        tituloVazio="Sem território no recorte"
        descricaoVazio="Nenhuma diretoria com cobertura registrada. Ajuste o filtro para ver outro recorte."
      >
        {(dado) => (
          <div className="space-y-4">
            <Secao titulo="Filtros">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="mapa-dre">DRE</Label>
                  <Select value={dreEscolhida} onValueChange={escolherDre}>
                    <SelectTrigger id="mapa-dre">
                      <SelectValue placeholder="Todas as DREs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODAS_AS_DRES}>Todas as DREs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <fieldset className="space-y-1.5">
                  <legend className="text-sm font-medium">Camadas</legend>
                  <div className="flex flex-wrap gap-4 pt-1">
                    {dado.camadas.map((camada) => (
                      <div key={camada.chave} className="flex items-center gap-2">
                        {/* Desabilitada: a camada só significa algo com o mapa
                            desenhado, e o desenho não vem nesta PR. */}
                        <Checkbox
                          id={`camada-${camada.chave}`}
                          checked={camada.ativa}
                          disabled
                          aria-describedby="aviso-camada-cartografica"
                        />
                        <Label htmlFor={`camada-${camada.chave}`} className="font-normal">
                          {camada.rotulo}
                        </Label>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>
            </Secao>

            <Secao titulo="Mapa">
              <div id="aviso-camada-cartografica">
                <EmptyState
                  icon={Layers}
                  title="O desenho do mapa entra com a decisão sobre a biblioteca cartográfica"
                  description="A estrutura da tela está pronta: filtro, camadas, resumo e legenda. O protótipo desenha com Leaflet sobre OpenStreetMap; acrescentar biblioteca de mapa e fonte externa de ladrilhos exige decisão registrada antes, e ela ainda não foi tomada."
                />
              </div>
            </Secao>

            <Secao titulo="Resumo">
              <ListaDeContagens
                itens={dado.resumo.map((item) => ({
                  status: item.rotulo,
                  quantidade: Number(item.valor),
                }))}
                ariaLabel="Resumo do recorte territorial"
              />
            </Secao>

            <Secao titulo="Legenda">
              <ul className="space-y-2">
                {dado.legenda.map((item) => (
                  <li key={item.rotulo} className="flex items-center gap-2 text-sm">
                    <span
                      className={`inline-block h-3 w-3 rounded-full ${COR_DA_LEGENDA[item.cor]}`}
                      aria-hidden="true"
                    />
                    {item.rotulo}
                  </li>
                ))}
              </ul>
            </Secao>
          </div>
        )}
      </QuadroDeEstado>
    </div>
  );
}

export default MapaInterativoPage;
